/**
 * @file Utilities for fetching sections and entries from withint the Patient Summary bundle.
 */

import React from 'react';
import { getByDisplayKey } from './displayKeyUtil';
import SectionEntryRow from '../components/EntryRow';
import SectionEmptyRow from '../components/EmptyRow';
import IDKeys from '../constants/idKeys';
import { sortByDateDescending } from './sortAndFilterUtil';

/**
 * Given a patient summary and config, return the section whose coding matches the coding in the config.
 * @param {string} ps The patient summary.
 * @param {object} sectionConfig Section of the summary we're looking at.
 * @returns {object} A section of the patient summary.
 */
export const getSectionByConfig = (ps, sectionConfig) =>
  ps?.entry[0]?.resource?.section?.find(
    (section) =>
      getByDisplayKey(section, 'code.coding.0.code') === sectionConfig.code,
  );

/**
 * Searches for entries within a section and returns a list of elements to display
 * @param {string} ps Patient Summary.
 * @param {object} section Section of the summary we're looking at.
 * @param {object} sectionConfig Config for the section.
 * @returns {React.ReactElement} A list of EntryRow elements to display on SectionPage
 */
export const getEntries = (ps, section, sectionConfig) => {
  const entryReferences =
    section?.entry?.map((entry) => entry?.reference) || [];

  // Get the entry object for each reference
  const entries = entryReferences
    .map((reference) =>
      ps?.entry?.find((psSection) => psSection?.fullUrl === reference),
    )
    .filter((entry) => {
      const filterCondition = sectionConfig.filterKey
        ? sectionConfig.filters.includes(
            getByDisplayKey(entry, sectionConfig.filterKey),
          )
        : true;
      const resourceTypeCondition = sectionConfig.resourceType
        ? sectionConfig.resourceType.includes(entry?.resource?.resourceType)
        : true;
      return filterCondition && resourceTypeCondition;
    });

  // Breakpoint to check if this section is empty
  // Mandatory PS sections (for our case Medication Summary) will always exist in the bundle with one entry
  if (
    !entries[0] ||
    entries[0]?.resource?.medicationCodeableConcept?.coding[0]?.code ===
      'no-medication-info'
  ) {
    return <SectionEmptyRow testID={`${IDKeys.SECTION_PAGE}EntryRow-0`} />;
  }

  const sortedEntries = sortByDateDescending(
    entries,
    sectionConfig.sortKey,
    sectionConfig.fallbackSortKey,
  );

  return sortedEntries.map((entry, i) => (
    <SectionEntryRow
      testID={`${IDKeys.SECTION_PAGE}EntryRow-${i}`}
      key={entry.fullUrl}
      entry={entry}
      entryConfig={sectionConfig.entry}
    />
  ));
};
